import { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek";
import moment from "moment";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import CombinedDateTimePicker from "../../components/CustomComponents/DateTimePicker/CombinedDateTimePicker";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";

// Helpers for time normalization
// Internal state (schedules/commonTimeSlot) uses 24-hour "HH:mm" strings
// Payload/DB uses 12-hour strings like "9:30 AM"

// Convert DB/payload time (12h or 24h) → state format "HH:mm"
const toStateTimeHHmm = (timeStr) => {
  if (!timeStr) return "";
  const trimmed = String(timeStr).trim();
  const parsed = moment(trimmed, ["h:mm A", "hh:mm A", "H:mm", "HH:mm"], true);
  if (!parsed.isValid()) return trimmed;
  return parsed.format("HH:mm");
};

// Convert state time "HH:mm" (or compatible) → payload/DB 12-hour format
const toPayloadTime12Hour = (timeStr) => {
  if (!timeStr) return "";
  const trimmed = String(timeStr).trim();
  const parsed = moment(trimmed, ["HH:mm", "H:mm", "h:mm A", "hh:mm A"], true);
  if (!parsed.isValid()) return trimmed;
  return parsed.format("h:mm A");
};

function AddAdvanceDeposit() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const { hasInitiallyLoaded } = useProfileAndSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const serviceAreaID = searchParams.get('serviceAreaID');
  const [loading, setLoading] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const [compCurrency, setCompCurrency] = useState("GBP");
  const [ruleData, setRuleData] = useState({
    ruleName: "",
    depositType: "FixedAmount",
    depositValue: "",
    isActive: true,
    priority: 0,
  });
  const [enableDateRange, setEnableDateRange] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDateTime: null,
    endDateTime: null,
  });
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [schedules, setSchedules] = useState(schedule);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    { from: "00:00", to: "23:59" },
  ]);
  const [enableMaxDeposit, setEnableMaxDeposit] = useState(false);
  const [enableMinDeposit, setEnableMinDeposit] = useState(false);
  const [maxDepositValue, setMaxDepositValue] = useState("");
  const [minDepositValue, setMinDepositValue] = useState("");
  const initialFormSnapshotRef = useRef(null);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      ruleData,
      enableDateRange: Boolean(enableDateRange),
      dateRange: {
        startDateTime: dateRange.startDateTime ? moment(dateRange.startDateTime).format() : null,
        endDateTime: dateRange.endDateTime ? moment(dateRange.endDateTime).format() : null,
      },
      enableSchedule: Boolean(enableSchedule),
      useSameTimeSlots: Boolean(useSameTimeSlots),
      schedules,
      commonTimeSlot,
      enableMaxDeposit: Boolean(enableMaxDeposit),
      enableMinDeposit: Boolean(enableMinDeposit),
      maxDepositValue: maxDepositValue ?? "",
      minDepositValue: minDepositValue ?? "",
    });
  }, [ruleData, enableDateRange, dateRange, enableSchedule, useSameTimeSlots, schedules, commonTimeSlot, enableMaxDeposit, enableMinDeposit, maxDepositValue, minDepositValue]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);


  const depositTypeOptions = [
    { value: "FixedAmount", label: "Fixed Amount" },
    { value: "PerPerson", label: "Per Person" },
  ];

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Fetch CompCurrency (only show error after initial load attempt; avoid toast while profile is still loading)
  const fetchCompCurrency = useCallback(async () => {
    try {
      if (!hasInitiallyLoaded) return;
      if (!profileData) {
        showErrorToast("Profile data not available. Please refresh the page.");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, [profileData, hasInitiallyLoaded]);

  useEffect(() => {
    fetchCompCurrency();
  }, [fetchCompCurrency]);

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Prevent negative values for numeric fields
    if (name === "depositValue" || name === "priority" || name === "maxDepositValue" || name === "minDepositValue") {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < 0) {
        newValue = "0";
      }
    }

    setRuleData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => {
      const momentValue = value && moment.isMoment(value) && value.isValid() ? value : null;
      const newDateRange = { ...prev, [field]: momentValue };

      if (
        field === "startDateTime" &&
        newDateRange.endDateTime &&
        moment.isMoment(newDateRange.endDateTime) &&
        newDateRange.endDateTime.isValid()
      ) {
        if (newDateRange.endDateTime.isBefore(momentValue)) {
          newDateRange.endDateTime = momentValue ? momentValue.clone() : null;
        }
      }

      return newDateRange;
    });
  };

  const handleEnableDateRangeToggle = (checked) => {
    if (checked) {
      const now = moment();
      setDateRange({
        startDateTime: now,
        endDateTime: now,
      });
    } else {
      setDateRange({
        startDateTime: null,
        endDateTime: null,
      });
    }
    setEnableDateRange(checked);
  };


  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;
    if (!ruleData.ruleName.trim()) {
      showErrorToast("Rule name is required");
      return false;
    }

    if (!ruleData.depositValue || parseFloat(ruleData.depositValue) <= 0) {
      showErrorToast("Valid deposit value greater than zero is required");
      return false;
    }

    if (enableDateRange) {
      if (!dateRange.startDateTime || !moment(dateRange.startDateTime).isValid()) {
        showErrorToast("Please select a valid start date");
        return false;
      }
      if (!dateRange.endDateTime || !moment(dateRange.endDateTime).isValid()) {
        showErrorToast("Please select a valid end date");
        return false;
      }
      if (moment(dateRange.endDateTime).isBefore(dateRange.startDateTime)) {
        showErrorToast("End date cannot be before start date");
        return false;
      }
    }

    if (enableMaxDeposit && (!maxDepositValue || parseFloat(maxDepositValue) <= 0)) {
      showErrorToast("Valid maximum deposit value is required when enabled");
      return false;
    }

    if (enableMinDeposit && (!minDepositValue || parseFloat(minDepositValue) <= 0)) {
      showErrorToast("Valid minimum deposit value is required when enabled");
      return false;
    }

    setLoading(true);

    try {
      let depositStartDate = null;
      let depositEndDate = null;
      if (enableDateRange) {
        if (dateRange.startDateTime && moment(dateRange.startDateTime).isValid()) {
          depositStartDate = moment(dateRange.startDateTime).format("YYYY-MM-DD");
        }
        if (dateRange.endDateTime && moment(dateRange.endDateTime).isValid()) {
          depositEndDate = moment(dateRange.endDateTime).format("YYYY-MM-DD");
        }
      }

      const formattedSchedules = [];
      if (enableSchedule) {
        if (useSameTimeSlots) {
          const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
          days.forEach((day) => {
            commonTimeSlot.forEach((slot) => {
              formattedSchedules.push({
                dayName: day,
                openTime: toPayloadTime12Hour(slot.from),
                closeTime: toPayloadTime12Hour(slot.to),
              });
            });
          });
        } else {
          schedules.forEach((schedule) => {
            schedule.timeSlots.forEach((slot) => {
              formattedSchedules.push({
                dayName: schedule.day,
                openTime: toPayloadTime12Hour(slot.from),
                closeTime: toPayloadTime12Hour(slot.to),
              });
            });
          });
        }
      }

      const payload = {
        id: 0,
        compID: CompID,
        ruleName: ruleData.ruleName,
        isActive: ruleData.isActive,
        isSetDateRange: enableDateRange,
        depositStartDate: depositStartDate,
        depositEndDate: depositEndDate,
        depositType: ruleData.depositType,
        depositValue: parseFloat(ruleData.depositValue),
        isSetMaxDepositValue: enableMaxDeposit,
        maxDepositValue: enableMaxDeposit ? parseFloat(maxDepositValue) : null,
        isSetMinDepositValue: enableMinDeposit,
        minDepositValue: enableMinDeposit ? parseFloat(minDepositValue) : null,
        priority: parseInt(ruleData.priority) || 0,
        serviceAreaID: serviceAreaID ? parseInt(serviceAreaID) : null,
        weeklyTimings: formattedSchedules,
      };

      const response = await axiosInstance.post(
        APIs.POST_ADD_UPDATE_ADVANCE_DEPOSIT_RULE,
        payload
      );

      if (response.data.status) {
        showSuccessToast("Advance deposit rule added successfully");
        initialFormSnapshotRef.current = getFormSnapshot();
        if (exitAfterSave) {
          if (serviceAreaID) {
            navigate(`/tableManagement/updateServiceArea/${serviceAreaID}`);
          } else {
            navigate("/tableManagement/manageAdvanceDeposit");
          }
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to add rule");
        return false;
      }
    } catch (err) {
      showErrorToast("Error adding advance deposit rule");
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  if (loading && !ruleData.ruleName) {
    return (<GridSectionLoader loading={true} minHeight="70vh" />);
  }

  return (
    <div>
      
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/tableManagement/manageAdvanceDeposit">Manage Advance Deposit Rules</Link>
            </li>
            <li className="breadcrumb-item active">Create Advance Deposit Rule</li>
          </ol>
          <h1 className="page-header mb-0">Create Advance Deposit Rule</h1>
        </div>
        <SaveActions
          onCancel={() =>
            serviceAreaID
              ? navigate(`/tableManagement/updateServiceArea/${serviceAreaID}`)
              : navigate("/tableManagement/manageAdvanceDeposit")
          }
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
<div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>General</PanelHeader>
          <PanelBody>
            <div 
              className="p-4 rounded-3 shadow-sm"
              style={{
                backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
              }}
            >
              {/* Basic Information Section */}
              <h5 className="card-title mb-3">Basic Information</h5>
              <div className="row mb-4">
                <div className="col-6">
                  <FormInput
                    label="Rule Name"
                    placeHolder="Enter rule name"
                    type="text"
                    name="ruleName"
                    value={ruleData.ruleName}
                    onchange={handleInputChange}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label">Deposit Type</label>
                  <Select
                    options={depositTypeOptions}
                    value={depositTypeOptions.find(opt => opt.value === ruleData.depositType)}
                    onChange={(selected) =>
                      setRuleData((prev) => ({
                        ...prev,
                        depositType: selected.value,
                      }))
                    }
                    styles={dropdownStyles}
                  />
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-6">
                  <FormInput
                    label={`Deposit Value ${ruleData.depositType === "Percentage" ? "(%)" : `(${getCurrencySymbol(compCurrency)})`}`}
                    placeHolder={ruleData.depositType === "Percentage" ? "0.00" : `${getCurrencySymbol(compCurrency)}0.00`}
                    type="number"
                    name="depositValue"
                    value={ruleData.depositValue}
                    onchange={handleInputChange}
                    step="0.01"
                    min={0}
                  />
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12">
                  <ToggleSwitch
                    isChecked={ruleData.isActive}
                    onToggle={(checked) =>
                      setRuleData((prev) => ({ ...prev, isActive: checked }))
                    }
                    label="Active"
                    name="isActive"
                    themeColor={themeColor}
                  />
                  <p className="text-muted mt-2">
                    Only active rules will be applied to reservations
                  </p>
                </div>
              </div>

              {/* Date Range Section */}
              <div className="mt-4">
                <h5 className="card-title mb-3">Date Range</h5>
                <div className="mb-3">
                  <ToggleSwitch
                    isChecked={enableDateRange}
                    onToggle={handleEnableDateRangeToggle}
                    label="Set Date Range"
                    name="enableDateRange"
                    themeColor={themeColor}
                  />
                  <p className="text-muted mt-2">
                    Apply this rule only within a specific date range
                  </p>
                </div>

                {enableDateRange && (
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <label className="form-label">Start Date</label>
                      <CombinedDateTimePicker
                        value={dateRange.startDateTime}
                        onChange={(value) => handleDateRangeChange("startDateTime", value)}
                        showTime={false}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">End Date</label>
                      <CombinedDateTimePicker
                        value={dateRange.endDateTime}
                        onChange={(value) => handleDateRangeChange("endDateTime", value)}
                        showTime={false}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Weekly Schedule Section */}
              <div className="mt-4">
                <h5 className="card-title mb-3">Weekly Schedule</h5>
                <div className="mb-3">
                  <ToggleSwitch
                    isChecked={enableSchedule}
                    onToggle={setEnableSchedule}
                    label="Set Schedule"
                    name="enableSchedule"
                    themeColor={themeColor}
                  />
                  <p className="text-muted mt-2">
                    Set the days of the week and times of day this rule is available
                  </p>
                </div>

                {enableSchedule && (
                  <div className="mb-4">
                    <div className="mb-4 mt-3">
                      <ToggleSwitch
                        isChecked={useSameTimeSlots}
                        onToggle={(checked) => {
                          setUseSameTimeSlots(checked);
                          if (checked) {
                            applyCommonTimeSlotToAll();
                          }
                        }}
                        label="Use same time slots for all days"
                        name="useSameTimeSlots"
                        themeColor={themeColor}
                      />
                    </div>
                    <hr />

                    {useSameTimeSlots ? (
                      <CommonTimeSlotForAllWeek
                        setCommonTimeSlots={setCommonTimeSlot}
                        commonTimeSlots={commonTimeSlot}
                        isProfilePage={false}
                      />
                    ) : (
                      <ScheduleManager
                        schedules={schedules}
                        setSchedules={setSchedules}
                        isProfilePage={false}
                        isDarkMode={isDarkMode}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>
</GridSectionLoader>

      {serviceAreaID && (
        <div className="alert alert-info mt-3" role="alert">
          <i className="fa fa-info-circle me-2"></i>
          This rule will be automatically associated with the current service area.
        </div>
      )}
    </div>
  );
}

export default AddAdvanceDeposit;
